import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  Button,
  Space,
  Typography,
  Divider,
  Tag,
  Alert,
  Row,
  Col,
  Card,
  message,
  Spin
} from 'antd';
import {
  BugOutlined,
  SlackOutlined,
  SendOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import apiClient from '../services/axiosConfig';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const IssueReporterModal = ({
  visible,
  onClose,
  apiDetails,
  capturedData,
  theme,
  currentTheme,
  reporter
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [checkingIntegrations, setCheckingIntegrations] = useState(true);
  const [availableEpics, setAvailableEpics] = useState([]);
  const [loadingEpics, setLoadingEpics] = useState(false);
  
  // Check integration status when modal opens
  useEffect(() => {
    if (visible && reporter?.userId) {
      checkIntegrations();
    }
  }, [visible, reporter?.userId]);

  // Load epics for default project when modal opens
  useEffect(() => {
    if (visible && integrationStatus?.jira?.defaultProject?.key) {
      fetchEpics(integrationStatus.jira.defaultProject.key);
    }
  }, [visible, integrationStatus?.jira?.defaultProject?.key]);

  const checkIntegrations = async () => {
    try {
      setCheckingIntegrations(true);
      const response = await apiClient.get('/integrations/status', {
        params: { userId: reporter.userId }
      });
      setIntegrationStatus(response.data.data);
    } catch (error) {
      console.error('Failed to check integrations:', error);
    } finally {
      setCheckingIntegrations(false);
    }
  };

  const fetchEpics = async (projectKey) => {
    if (!projectKey || !reporter?.userId) return;
    
    try {
      setLoadingEpics(true);
      const response = await apiClient.get(`/integrations/jira/projects/${projectKey}/epics`, {
        params: { userId: reporter.userId }
      });
      setAvailableEpics(response.data.epics || []);
    } catch (error) {
      console.error('Failed to fetch epics:', error);
      setAvailableEpics([]);
    } finally {
      setLoadingEpics(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const issueData = {
        apiConfigId: apiDetails._id,
        apiDetails: {
          name: apiDetails.name,
          group: apiDetails.group,
          endpoint: apiDetails.route,
          method: apiDetails.method
        },
        issue: {
          type: values.issueType,
          title: values.title,
          description: values.description,
          expectedBehavior: values.expectedBehavior,
          actualBehavior: values.actualBehavior,
          priority: values.priority,
          tags: values.tags || []
        },
        capturedData: {
          request: capturedData.request,
          response: {
            ...capturedData.response,
            status: 200 // Force numeric status
          },
          timestamp: capturedData.timestamp || new Date()
        },
        reporter: reporter,
        sendToSlack: Boolean(values.sendToSlack),
        sendToJira: Boolean(values.sendToJira),
        slackChannel: values.slackChannel,
        jiraProject: values.jiraProject,
        jiraEpic: values.jiraEpic
      };
      
      const response = await apiClient.post('/issues', issueData);
      
      if (response.data.success) {
        message.success('Issue reported successfully!');
        
        // Show integration results
        if (response.data.integrations.slack) {
          message.info('Issue sent to Slack');
        }
        if (response.data.integrations.jira) {
          message.info('Issue created in Jira');
        }
        
        form.resetFields();
        onClose();
      }
    } catch (error) {
      console.error('Failed to report issue:', error);
      message.error('Failed to report issue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const issueTypes = [
    { value: 'wrong-structure', label: 'Wrong Response Structure' },
    { value: 'missing-fields', label: 'Missing Fields' },
    { value: 'incorrect-data', label: 'Incorrect Data' },
    { value: 'business-logic', label: 'Business Logic Issue' },
    { value: 'documentation-mismatch', label: 'Documentation Mismatch' },
    { value: 'performance', label: 'Performance Issue' },
    { value: 'other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'green' },
    { value: 'medium', label: 'Medium', color: 'orange' },
    { value: 'high', label: 'High', color: 'red' },
    { value: 'critical', label: 'Critical', color: 'magenta' }
  ];

  return (
    <Modal
      title={
        <Space>
          <BugOutlined style={{ color: theme.error }} />
          <span>Report API Issue</span>
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      style={{ top: 20 }}
    >
      {checkingIntegrations ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: '16px', color: theme.textSecondary }}>
            Checking integrations...
          </Text>
        </div>
      ) : (
        <>
          {/* API Details Card */}
          <Card
            size="small"
            style={{
              marginBottom: '20px',
              backgroundColor: theme.surfaceLight,
              borderColor: theme.border
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Text strong style={{ color: theme.text }}>API:</Text>{' '}
                <Text style={{ color: theme.textSecondary }}>{apiDetails?.name}</Text>
              </Col>
              <Col span={12}>
                <Text strong style={{ color: theme.text }}>Group:</Text>{' '}
                <Text style={{ color: theme.textSecondary }}>{apiDetails?.group}</Text>
              </Col>
              <Col span={12}>
                <Text strong style={{ color: theme.text }}>Method:</Text>{' '}
                <Tag color={apiDetails?.method === 'GET' ? 'green' : 'blue'}>
                  {apiDetails?.method}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong style={{ color: theme.text }}>Endpoint:</Text>{' '}
                <Text code style={{ fontSize: '12px' }}>{apiDetails?.route}</Text>
              </Col>
            </Row>
          </Card>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              priority: 'medium',
              sendToSlack: integrationStatus?.slack?.connected && integrationStatus?.slack?.defaultChannel,
              sendToJira: integrationStatus?.jira?.connected && integrationStatus?.jira?.defaultProject,
              slackChannel: integrationStatus?.slack?.defaultChannel?.id,
              jiraProject: integrationStatus?.jira?.defaultProject?.key,
              jiraEpic: integrationStatus?.jira?.defaultEpic?.key
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="issueType"
                  label="Issue Type"
                  rules={[{ required: true, message: 'Please select issue type' }]}
                >
                  <Select placeholder="Select issue type">
                    {issueTypes.map(type => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="priority"
                  label="Priority"
                  rules={[{ required: true }]}
                >
                  <Select>
                    {priorityOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        <Tag color={option.color}>{option.label}</Tag>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="title"
              label="Issue Title"
              rules={[{ required: true, message: 'Please provide a brief title' }]}
            >
              <Input placeholder="Brief summary of the issue" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please describe the issue' }]}
            >
              <TextArea
                rows={3}
                placeholder="Describe what's wrong with the API response..."
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="expectedBehavior"
                  label={
                    <span>
                      Expected Behavior{' '}
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        (Optional)
                      </Text>
                    </span>
                  }
                >
                  <TextArea
                    rows={2}
                    placeholder="What should the API return?"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="actualBehavior"
                  label={
                    <span>
                      Actual Behavior{' '}
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        (Optional)
                      </Text>
                    </span>
                  }
                >
                  <TextArea
                    rows={2}
                    placeholder="What did the API actually return?"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="tags"
              label={
                <span>
                  Tags{' '}
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    (Optional - press Enter to add)
                  </Text>
                </span>
              }
            >
              <Select
                mode="tags"
                placeholder="Add tags for categorization"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Divider style={{ margin: '20px 0' }} />

            {/* Integration Options */}
            <Title level={5} style={{ marginBottom: '16px', color: theme.text }}>
              Send To
            </Title>

            {(!integrationStatus?.slack?.connected && !integrationStatus?.jira?.connected) ? (
              <Alert
                message="No integrations connected"
                description="Connect Slack or Jira in the Integrations panel to send issues to your team."
                type="info"
                showIcon
                style={{ marginBottom: '20px' }}
                action={
                  <Button size="small" onClick={onClose}>
                    Go to Integrations
                  </Button>
                }
              />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {integrationStatus?.slack?.connected && (
                  <Card
                    size="small"
                    style={{
                      backgroundColor: theme.surfaceLight,
                      borderColor: theme.border,
                      marginBottom: '12px'
                    }}
                  >
                    <Form.Item
                      name="sendToSlack"
                      valuePropName="checked"
                      style={{ marginBottom: '8px' }}
                    >
                      <Checkbox>
                        <Space>
                          <SlackOutlined style={{ color: '#4A154B' }} />
                          Send to Slack
                        </Space>
                      </Checkbox>
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.sendToSlack !== currentValues.sendToSlack
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue('sendToSlack') && (
                          <Form.Item
                            name="slackChannel"
                            label="Channel"
                            style={{ marginBottom: 0, marginLeft: '24px' }}
                          >
                            <Select
                              placeholder="Use default channel"
                              style={{ width: '200px' }}
                              allowClear
                            >
                              <Option value={integrationStatus.slack.defaultChannel?.id}>
                                #{integrationStatus.slack.defaultChannel?.name} (default)
                              </Option>
                            </Select>
                          </Form.Item>
                        )
                      }
                    </Form.Item>
                  </Card>
                )}

                {integrationStatus?.jira?.connected && (
                  <Card
                    size="small"
                    style={{
                      backgroundColor: theme.surfaceLight,
                      borderColor: theme.border
                    }}
                  >
                    <Form.Item
                      name="sendToJira"
                      valuePropName="checked"
                      style={{ marginBottom: '8px' }}
                    >
                      <Checkbox>
                        <Space>
                          <img
                            src="https://cdn.worldvectorlogo.com/logos/jira-1.svg"
                            alt="Jira"
                            style={{ width: 16, height: 16 }}
                          />
                          Create Jira Issue
                        </Space>
                      </Checkbox>
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.sendToJira !== currentValues.sendToJira ||
                        prevValues.jiraProject !== currentValues.jiraProject
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue('sendToJira') && (
                          <>
                            <Form.Item
                              name="jiraProject"
                              label="Project"
                              style={{ marginBottom: '8px', marginLeft: '24px' }}
                            >
                              <Select
                                placeholder="Select project"
                                style={{ width: '200px' }}
                                onChange={(value) => {
                                  // Clear epic selection when project changes
                                  form.setFieldsValue({ jiraEpic: undefined });
                                  // Fetch epics for the selected project
                                  fetchEpics(value);
                                }}
                              >
                                {integrationStatus.jira.availableProjects?.map(project => (
                                  <Option key={project.key} value={project.key}>
                                    {project.name} ({project.key})
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                            
                            <Form.Item
                              name="jiraEpic"
                              label="Epic"
                              style={{ marginBottom: 0, marginLeft: '24px' }}
                            >
                              <Select
                                placeholder="Select epic (optional)"
                                style={{ width: '200px' }}
                                allowClear
                                loading={loadingEpics}
                                disabled={!getFieldValue('jiraProject')}
                              >
                                {availableEpics.map(epic => (
                                  <Option key={epic.key} value={epic.key}>
                                    {epic.name} ({epic.key})
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </>
                        )
                      }
                    </Form.Item>
                  </Card>
                )}
              </Space>
            )}

            {/* Form Actions */}
            <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SendOutlined />}
                >
                  Report Issue
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default IssueReporterModal;